import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import {
  AssignmentTurnedInRounded,
  CheckCircleRounded,
  EventAvailableRounded,
  LaptopMacRounded,
  MeetingRoomRounded,
  QuizRounded,
} from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { instantToZonedWall } from '@dosfilos/shared';
import { useTenantStore, useTenantTimeZone } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import {
  attendanceApi,
  type AttendanceStatus,
  type Modality,
  type StudentDayDto,
} from '../../../lib/api/attendance.api';
import type { StructureItemDto } from '../../../lib/api/lms.api';
import { useSectionStructure } from '../hooks/use-portal-data';
import { useClassroomOn } from '../hooks/use-classroom-on';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { courseColor } from './CourseShell';
import { formatDate } from '../../../lib/i18n-format';
import { usePortalCacheScope, usePortalViewing } from '../../../stores/portal-view.store';

/** A module's agenda entry: a class day or a task due that day. */
type AgendaEntry =
  | { kind: 'class'; date: string; day: StudentDayDto }
  | { kind: 'task'; date: string; item: StructureItemDto };

const TASK_STATUS_LABEL: Record<string, { key: string; fallback: string }> = {
  none: { key: 'portal.sessions.taskPending', fallback: 'Pendiente' },
  draft: { key: 'portal.sessions.taskDraft', fallback: 'Borrador' },
  submitted: { key: 'portal.sessions.taskSubmitted', fallback: 'Entregada' },
  graded: { key: 'portal.sessions.taskGraded', fallback: 'Calificada' },
  returned: { key: 'portal.sessions.taskReturned', fallback: 'Devuelta' },
};

const STATUS_META: Record<
  AttendanceStatus,
  { key: string; fallback: string; color: 'success' | 'error' | 'warning' | 'info' }
> = {
  present: { key: 'portal.sessions.status.present', fallback: 'Presente', color: 'success' },
  absent: { key: 'portal.sessions.status.absent', fallback: 'Ausente', color: 'error' },
  late: { key: 'portal.sessions.status.late', fallback: 'Tarde', color: 'warning' },
  excused: { key: 'portal.sessions.status.excused', fallback: 'Justificado', color: 'info' },
};

function dateLabel(ymd: string): string {
  return formatDate(ymd, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export function CourseSessionsPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { sectionId } = useParams<{ sectionId: string }>();
  const navigate = useNavigate();
  // «Ver portal»: sin marcar asistencia a nombre del alumno ni abrir tareas.
  const viewing = usePortalViewing();
  const scope = usePortalCacheScope();
  const { currentTenantId } = useTenantStore();
  const tz = useTenantTimeZone();
  // The day a task is due, where the seminary is. `dueAt.slice(0, 10)` is the UTC
  // day, and 23:59 in Chile is already tomorrow in UTC: the agenda showed a task
  // due Sep 25 as "Sep 26" and sorted it after the next day's class.
  const dueDay = (iso: string): string => instantToZonedWall(iso, tz).slice(0, 10);
  const { personId } = useStudentIdentity();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['portal-v2-days', sectionId, scope, personId],
    queryFn: () =>
      attendanceApi.getStudentCourseDays({
        tenantId: currentTenantId!,
        studentId: personId!,
        sectionId: sectionId!,
      }),
    enabled: !!currentTenantId && !!personId && !!sectionId,
    staleTime: 30_000,
  });

  // Structure gives us each module's assignment items (with due date + this
  // student's submission status) so the agenda can interleave tasks with days.
  const structQ = useSectionStructure(sectionId);
  // Sin aula, la agenda muestra los días de clase y no las tareas.
  const classroom = useClassroomOn();
  const tasksByModule = new Map<string, StructureItemDto[]>();
  for (const m of classroom.on ? (structQ.data?.modules ?? []) : []) {
    const tasks = m.items.filter((it) => it.type === 'assignment' && it.assignment?.dueAt);
    if (tasks.length) tasksByModule.set(m.id, tasks);
  }

  const selfMark = useMutation({
    mutationFn: (input: { moduleId: string; date: string; status: 'present' | 'absent' }) =>
      attendanceApi.selfMarkDayAttendance({
        tenantId: currentTenantId!,
        moduleId: input.moduleId,
        date: input.date,
        status: input.status,
      }),
    onSuccess: () => {
      setConfirm(null);
      void qc.invalidateQueries({ queryKey: ['portal-v2-days', sectionId, scope, personId] });
    },
  });

  // Attendance is a self-declaration → always ask the student to confirm before
  // recording (or undoing) it.
  const [confirm, setConfirm] = useState<{
    moduleId: string;
    date: string;
    dateLabel: string;
    status: 'present' | 'absent';
  } | null>(null);

  if (isLoading) return <PortalLoading />;
  const modules = data?.modules ?? [];
  const hasDays = modules.some((m) => m.days.length > 0);
  if (!hasDays) {
    return (
      <PortalEmpty
        icon={<EventAvailableRounded fontSize="inherit" />}
        title={t('portal.sessions.emptyTitle', { defaultValue: 'Aún no hay clases' })}
        body={t('portal.sessions.emptyBody', {
          defaultValue: 'Cuando el profesor programe las clases, aparecerán aquí por módulo.',
        })}
      />
    );
  }

  const color = sectionId ? courseColor(sectionId) : '#6366f1';

  // My attendance summary (over DAYS that have a mark — present/absent/late;
  // excused and unmarked don't count against me).
  const allDays = modules.flatMap((m) => m.days);
  const markedDays = allDays.filter(
    (d) => d.status === 'present' || d.status === 'absent' || d.status === 'late',
  ).length;
  const presentDays = allDays.filter((d) => d.status === 'present' || d.status === 'late').length;
  const myPct = markedDays > 0 ? Math.round((presentDays / markedDays) * 100) : null;
  const minPct = data?.minPct ?? 75;
  const atRisk = myPct !== null && myPct < minPct;

  const modalityIcon = (m: Modality) =>
    m === 'in-person' ? (
      <MeetingRoomRounded sx={{ fontSize: 15 }} />
    ) : (
      <LaptopMacRounded sx={{ fontSize: 15 }} />
    );
  const modalityLabel = (m: Modality) =>
    m === 'in-person'
      ? t('portal.sessions.inPerson', { defaultValue: 'Presencial' })
      : m === 'hybrid'
        ? t('portal.sessions.hybrid', { defaultValue: 'Híbrida' })
        : t('portal.sessions.online', { defaultValue: 'Online' });

  const renderDay = (d: StudentDayDto) => {
    const status = d.status === 'none' ? null : STATUS_META[d.status];
    const isPresent = d.status === 'present';
    return (
      <Box
        key={`${d.moduleId}__${d.date}`}
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid #21262d',
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="#e6edf3" fontWeight={600}>
                {dateLabel(d.date)}
              </Typography>
              <Typography variant="caption" color="#8b949e">
                {d.startTime}–{d.endTime}
              </Typography>
              <Chip
                size="small"
                icon={modalityIcon(d.modality)}
                label={modalityLabel(d.modality)}
                variant="outlined"
                sx={{ height: 18, fontSize: 10, borderColor: '#30363d', color: '#8b949e' }}
              />
            </Stack>
            <Typography variant="caption" color="#8b949e" display="block" sx={{ mt: 0.25 }}>
              {d.topics.join(' · ')}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            {status && (
              <Chip size="small" label={t(status.key, status.fallback)} color={status.color} />
            )}
            {/* Self-mark for online/remote class days within the window. */}
            {!viewing &&
              d.canSelfMark &&
              (isPresent ? (
                <Button
                  size="small"
                  startIcon={<CheckCircleRounded sx={{ fontSize: 16 }} />}
                  color="success"
                  disabled={selfMark.isPending}
                  onClick={() =>
                    setConfirm({
                      moduleId: d.moduleId,
                      date: d.date,
                      dateLabel: dateLabel(d.date),
                      status: 'absent',
                    })
                  }
                >
                  {t('portal.sessions.attended', { defaultValue: 'Asististe' })}
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={selfMark.isPending}
                  onClick={() =>
                    setConfirm({
                      moduleId: d.moduleId,
                      date: d.date,
                      dateLabel: dateLabel(d.date),
                      status: 'present',
                    })
                  }
                >
                  {t('portal.sessions.markAttendance', { defaultValue: 'Marcar asistencia' })}
                </Button>
              ))}
          </Stack>
        </Stack>
      </Box>
    );
  };

  const renderTask = (item: StructureItemDto) => {
    const dueIso = item.assignment?.dueAt;
    const due = dueIso ? dateLabel(dueDay(dueIso)) : '';
    const sub = item.assignment?.submissionStatus ?? 'none';
    const st = TASK_STATUS_LABEL[sub] ?? TASK_STATUS_LABEL['none']!;
    return (
      <Box
        key={`task-${item.id}`}
        onClick={() =>
          !viewing && navigate(`/portal-v2/courses/${sectionId}/assignments/${item.targetId}`)
        }
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid #21262d',
          borderLeft: '3px solid rgba(245,158,11,0.5)',
          cursor: viewing ? 'default' : 'pointer',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box sx={{ color: '#fbbf24', display: 'flex' }}>
            {item.assignment?.isQuiz ? (
              <QuizRounded sx={{ fontSize: 18 }} />
            ) : (
              <AssignmentTurnedInRounded sx={{ fontSize: 18 }} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" color="#e6edf3" fontWeight={500}>
              {item.title}
            </Typography>
            <Typography variant="caption" color="#8b949e">
              {t('portal.sessions.taskDue', { date: due, defaultValue: `Entrega: ${due}` })}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={t(st.key, st.fallback)}
            color={sub === 'graded' ? 'success' : sub === 'submitted' ? 'info' : 'warning'}
            variant="outlined"
          />
        </Stack>
      </Box>
    );
  };

  const buildEntries = (m: (typeof modules)[number]): AgendaEntry[] => {
    const dayEntries: AgendaEntry[] = m.days.map((d) => ({ kind: 'class', date: d.date, day: d }));
    const taskEntries: AgendaEntry[] = (tasksByModule.get(m.moduleId) ?? []).map((it) => ({
      kind: 'task',
      date: dueDay(it.assignment!.dueAt!),
      item: it,
    }));
    return [...dayEntries, ...taskEntries].sort(
      (a, b) => a.date.localeCompare(b.date) || (a.kind === 'class' ? -1 : 1),
    );
  };

  return (
    <Stack spacing={2}>
      {myPct !== null && (
        <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography variant="overline" color="#8b949e" fontWeight={700}>
                {t('portal.sessions.myAttendance', { defaultValue: 'Mi asistencia' })}
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography variant="h5" fontWeight={800} color={atRisk ? '#f87171' : '#34d399'}>
                  {myPct}%
                </Typography>
                <Typography variant="caption" color="#8b949e">
                  {t('portal.sessions.attendanceCount', {
                    present: presentDays,
                    total: markedDays,
                    defaultValue: `${presentDays} de ${markedDays} días`,
                  })}
                </Typography>
              </Stack>
            </Box>
            {atRisk && (
              <Chip
                color="warning"
                label={t('portal.sessions.atRisk', {
                  min: minPct,
                  defaultValue: `En riesgo — bajo el ${minPct}% requerido`,
                })}
              />
            )}
          </Stack>
        </Card>
      )}
      {modules
        .filter((m) => m.days.length > 0)
        .map((m, mIdx) => (
          <Card
            key={m.moduleId}
            sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2 }}
          >
            <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #21262d' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {mIdx + 1}
                </Box>
                <Typography variant="subtitle1" fontWeight={700} color="#e6edf3" sx={{ flex: 1 }}>
                  {m.title}
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate(`/portal-v2/courses/${sectionId}/modules`)}
                  sx={{ textTransform: 'none', color: '#8b949e', fontSize: 12 }}
                >
                  {t('portal.sessions.viewContent', { defaultValue: 'Ver contenido →' })}
                </Button>
              </Stack>
            </Box>
            {buildEntries(m).map((e) =>
              e.kind === 'class' ? renderDay(e.day) : renderTask(e.item),
            )}
          </Card>
        ))}

      <Dialog open={!!confirm} onClose={selfMark.isPending ? undefined : () => setConfirm(null)}>
        <DialogTitle>
          {confirm?.status === 'present'
            ? t('portal.sessions.confirmTitle', { defaultValue: 'Confirmar asistencia' })
            : t('portal.sessions.confirmUndoTitle', { defaultValue: 'Quitar asistencia' })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm?.status === 'present'
              ? t('portal.sessions.confirmBody', {
                  date: confirm?.dateLabel ?? '',
                  defaultValue: `¿Confirmas que asististe a la clase del ${confirm?.dateLabel}?`,
                })
              : t('portal.sessions.confirmUndoBody', {
                  date: confirm?.dateLabel ?? '',
                  defaultValue: `¿Quitar tu asistencia de la clase del ${confirm?.dateLabel}?`,
                })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={selfMark.isPending}>
            {t('portal.sessions.cancel', { defaultValue: 'Cancelar' })}
          </Button>
          <Button
            variant="contained"
            color={confirm?.status === 'present' ? 'success' : 'warning'}
            disabled={selfMark.isPending}
            onClick={() =>
              confirm &&
              selfMark.mutate({
                moduleId: confirm.moduleId,
                date: confirm.date,
                status: confirm.status,
              })
            }
          >
            {selfMark.isPending
              ? t('portal.sessions.confirming', { defaultValue: 'Guardando…' })
              : confirm?.status === 'present'
                ? t('portal.sessions.confirm', { defaultValue: 'Sí, asistí' })
                : t('portal.sessions.confirmUndo', { defaultValue: 'Sí, quitar' })}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
